import { getFeatures } from '@/api/features'
import MainLayout from '@/components/layout/MainLayout.vue'
import {
  DEFAULT_ROUTE,
  PUBLIC_ROUTE_NAMES,
} from '@/constants/routeConfig'
import {
  applyRoutePermissions,
  flattenRoutesForPermissionAudit,
  requiredPermissionsText,
} from '@/router/permissions'
import { useAuthStore } from '@/stores/auth'
import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'
import { logger } from '@/utils/logger'

export const routes: RouteRecordRaw[] = [
  // 登录页（独立布局，不使用 MainLayout）
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/error/Forbidden.vue'),
    meta: { requiresAuth: false, permissions: [], title: '无权访问' }
  },
  // 全屏控制台（独立布局，脱离 MainLayout）
  {
    path: '/console',
    name: 'agent-console',
    component: () => import('@/views/console/AgentConsoleView.vue'),
    meta: { title: '智能体控制台', fullscreen: true, menuGroup: 'operations' }
  },
  {
    path: '/chat/:code',
    name: 'PublicChat',
    component: () => import('@/views/PublicChatView.vue'),
    meta: { requiresAuth: false, fullscreen: true, title: '工作流对话' }
  },
  {
    path: '/dashboard-generator',
    name: 'dashboard-generator-standalone',
    component: () => import('@/views/DashboardGeneratorView.vue'),
    meta: { title: '收藏看板', fullscreen: true, feature: 'chatbi_v3' }
  },
  {
    path: '/',
    component: MainLayout,
    redirect: DEFAULT_ROUTE,
    children: [
      {
        path: '',
        name: 'home',
        redirect: DEFAULT_ROUTE
      },
      {
        path: 'workbench/document-search',
        name: 'workbench-document-search',
        component: () => import('@/views/workbench/DocumentSearchWorkbenchView.vue'),
        meta: { title: '文档检索', menuGroup: 'workbench' }
      },
      {
        path: 'command-center',
        name: 'command-center',
        component: () => import('@/views/CommandCenterView.vue'),
        meta: { title: '指挥中心', menuGroup: 'ops-center' }
      },
      {
        path: 'operations-cockpit',
        name: 'operations-cockpit',
        component: () => import('@/views/OperationsCockpitView.vue'),
        meta: { title: '运营驾驶舱', menuGroup: 'ops-center' }
      },
      // ===== 四大产品域路由 =====
      // 一、知识与语义（agent 域）
      {
        path: 'knowledge/knowledge-base',
        name: 'knowledge-base',
        component: () => import('@/views/KnowledgeBaseView.vue'),
        meta: { menuGroup: 'knowledge' }
      },
      {
        path: 'knowledge/knowledge-base/create',
        name: 'knowledge-base-create',
        component: () => import('@/views/KnowledgeBaseCreateView.vue'),
        meta: { menuGroup: 'knowledge', title: '创建知识库' }
      },
      {
        path: 'knowledge/knowledge-base/:id',
        name: 'knowledge-base-detail',
        component: () => import('@/views/KnowledgeBaseDetailView.vue'),
        meta: { menuGroup: 'knowledge' }
      },
      {
        path: 'knowledge/knowledge-base/:id/resource/:resourceId',
        name: 'resource-qa-detail',
        component: () => import('@/views/ResourceQADetailView.vue'),
        meta: { menuGroup: 'knowledge' }
      },
      // SQL 语料库已合并到 SQL 示例，旧路由重定向
      {
        path: 'knowledge/sql-corpus',
        redirect: '/knowledge/sql-examples'
      },
      {
        path: 'knowledge/sql-examples',
        name: 'governance-sql-examples',
        component: () => import('@/views/SqlExamplesView.vue'),
        meta: { menuGroup: 'knowledge' }
      },
      {
        path: 'knowledge/metrics',
        name: 'governance-metrics',
        component: () => import('@/views/MetricsView.vue'),
        meta: { menuGroup: 'knowledge' }
      },
      {
        path: 'knowledge/tables-metadata',
        name: 'governance-tables-metadata',
        component: () => import('@/views/TablesMetadataView.vue'),
        meta: { menuGroup: 'ai-datalake' }
      },
      {
        path: 'knowledge/instructions',
        name: 'governance-instructions',
        component: () => import('@/views/InstructionsView.vue'),
        meta: { menuGroup: 'knowledge' }
      },
      {
        path: 'knowledge/dimensions',
        name: 'governance-dimensions',
        component: () => import('@/views/DimensionsView.vue'),
        meta: { title: '维度', menuGroup: 'knowledge' }
      },
      // 旧路由重定向（兼容性）
      {
        path: 'knowledge/dimension-synonyms',
        redirect: '/knowledge/dimensions'
      },
      {
        path: 'knowledge/dimension-values',
        redirect: '/knowledge/dimensions'
      },
      {
        path: 'knowledge/business-terms',
        name: 'governance-business-terms',
        component: () => import('@/views/BusinessTermsView.vue'),
        meta: { menuGroup: 'knowledge' }
      },
      {
        path: 'knowledge/subject-tree',
        name: 'governance-subject-tree',
        component: () => import('@/views/SubjectTreeView.vue'),
        meta: { title: '主题树', menuGroup: 'knowledge' }
      },
      {
        path: 'knowledge/report-templates',
        name: 'governance-report-templates',
        component: () => import('@/views/ReportTemplatesView.vue'),
        meta: { title: '报表模板', menuGroup: 'knowledge' }
      },
      // 一、b. 收藏看板管理（全项目）
      {
        path: 'admin/favorites',
        name: 'governance-admin-favorites',
        component: () => import('@/views/governance/AdminFavoritesView.vue'),
        meta: { title: '收藏看板管理', menuGroup: 'knowledge' }
      },
      // 二、模型管理（agent 域）
      {
        path: 'agent/model',
        name: 'governance-model',
        component: () => import('@/views/governance/ModelConfigView.vue'),
        meta: { title: '模型配置', menuGroup: 'model-mgmt' }
      },
      // 三、AI 数据湖路由（knowledge 域）
      {
        path: 'knowledge/datalake/overview',
        name: 'datalake-overview',
        component: () => import('@/views/governance/datalake/DatalakeOverviewView.vue'),
        meta: { title: '数据湖总览', menuGroup: 'governance' }
      },
      {
        path: 'knowledge/datalake/datasets',
        name: 'datalake-datasets',
        component: () => import('@/views/governance/datalake/DatalakeDatasetView.vue'),
        meta: { title: '数据集', menuGroup: 'ai-datalake' }
      },
      {
        path: 'knowledge/datalake/catalog',
        name: 'datalake-catalog',
        redirect: '/knowledge/datalake/unified-catalog',
        meta: { title: '数据目录', menuGroup: 'ai-datalake' }
      },
      {
        path: 'knowledge/datalake/unified-catalog',
        name: 'unified-catalog',
        component: () => import('@/views/governance/datalake/UnifiedCatalogView.vue'),
        meta: { title: '数据目录', menuGroup: 'ai-datalake' }
      },
      {
        path: 'knowledge/datalake/track-catalog',
        name: 'track-catalog',
        component: () => import('@/views/governance/datalake/TrackCatalogView.vue'),
        meta: { title: '埋点目录', menuGroup: 'ai-datalake' }
      },
      // 旧路由重定向（数据治理 → AI 数据湖）
      {
        path: 'knowledge/data/assets',
        redirect: '/knowledge/datalake/datasets'
      },
      {
        path: 'knowledge/data/metadata',
        redirect: '/knowledge/tables-metadata'
      },
      // 四、管控中心（control 域）
      {
        path: 'control/projects',
        name: 'governance-projects',
        component: () => import('@/views/ProjectManagementView.vue'),
        meta: { title: '项目管理', menuGroup: 'admin', requiresAdmin: true }
      },
      {
        path: 'control/projects/:projectId/ingest',
        name: 'governance-project-ingest',
        component: () => import('@/views/IngestPipelineView.vue'),
        meta: { title: 'Pipeline 管理', menuGroup: 'admin', requiresAdmin: true }
      },
      // 权限控制台（117-05：role/binding/override + audit + UX）
      {
        path: 'control/permissions',
        name: 'governance-permissions-home',
        component: () => import('@/views/control/permissions/PermissionsHomeView.vue'),
        meta: { title: '权限概览', menuGroup: 'admin', requiresAdmin: true }
      },
      {
        path: 'control/permissions/users',
        name: 'governance-permissions-users',
        component: () => import('@/views/control/permissions/UsersListView.vue'),
        meta: { title: '用户授权', menuGroup: 'admin', requiresAdmin: true }
      },
      {
        path: 'control/permissions/agents',
        name: 'governance-permissions-agents',
        component: () => import('@/views/control/permissions/AgentsListView.vue'),
        meta: { title: '智能体授权', menuGroup: 'admin', requiresAdmin: true }
      },
      {
        path: 'control/permissions/roles',
        name: 'governance-permissions-roles',
        component: () => import('@/views/control/permissions/RolesListView.vue'),
        meta: { title: '角色定义', menuGroup: 'admin', requiresAdmin: true }
      },
      {
        path: 'control/permissions/catalog',
        name: 'governance-permissions-catalog',
        component: () => import('@/views/control/permissions/PermissionsCatalogView.vue'),
        meta: { title: '权限项参考', menuGroup: 'admin', requiresAdmin: true }
      },
      // 五、执行中心（mission 域）
      {
        path: 'mission/tracing',
        name: 'governance-tracing',
        component: () => import('@/views/mission/ExecutionOverviewView.vue'),
        meta: { title: '执行概览', menuGroup: 'ops-center' }
      },
      {
        path: 'mission/tracing/:traceId',
        name: 'governance-trace-detail',
        component: () => import('@/views/mission/ExecutionTraceDetailView.vue'),
        meta: { title: '执行追踪详情', menuGroup: 'ops-center' }
      },
      // 会话追踪旧路由 → 重定向到执行概览
      {
        path: 'mission/sessions',
        redirect: '/mission/tracing'
      },
      {
        path: 'mission/pending-tasks',
        name: 'governance-pending-tasks',
        component: () => import('@/views/mission/PendingTasksView.vue'),
        meta: { title: '待处理任务', menuGroup: 'ops-center' }
      },
      {
        path: 'mission/tasks',
        name: 'mission-task-center',
        component: () => import('@/views/mission/TaskCenterView.vue'),
        meta: { title: '任务中心', menuGroup: 'ops-center' }
      },
      {
        path: 'mission/tasks/:taskId',
        name: 'mission-task-detail',
        component: () => import('@/views/mission/TaskCenterView.vue'),
        meta: { title: '任务详情', menuGroup: 'ops-center' }
      },
      {
        path: 'control/memory/openviking',
        name: 'memory-openviking',
        component: () => import('@/views/governance/OpenVikingMemoryManagementView.vue'),
        meta: { title: 'OpenViking 记忆管理', menuGroup: 'agent', requiresAdmin: true }
      },
      {
        path: 'agent/prompt-manager',
        name: 'governance-prompt-manager',
        component: () => import('@/views/PromptManagerView.vue'),
        meta: { title: '提示词管理', menuGroup: 'agent-mgmt' }
      },
      {
        path: 'agent/playground',
        name: 'governance-playground',
        component: () => import('@/views/governance/PlaygroundView.vue'),
        meta: { title: '提示词调试台', menuGroup: 'agent-mgmt' }
      },
      {
        path: 'mission/scheduler',
        name: 'governance-scheduler',
        component: () => import('@/views/governance/SchedulerView.vue'),
        meta: { title: '周期任务', menuGroup: 'ops-center' }
      },
      // V10.2 W1.1：scheduler 执行历史日志查看
      {
        path: 'mission/scheduler/runs/:runId/logs',
        name: 'governance-scheduler-run-logs',
        component: () => import('@/views/governance/JobLogsView.vue'),
        meta: { title: '任务运行日志', menuGroup: 'ops-center' }
      },
      // V10.2 W2.3：scheduler 版本历史 + 回滚
      {
        path: 'mission/scheduler/jobs/:jobId/versions',
        name: 'governance-scheduler-job-versions',
        component: () => import('@/views/governance/JobVersionsView.vue'),
        meta: { title: 'Job 版本历史', menuGroup: 'ops-center' }
      },
      // V14 Chunk 3：scheduler 血缘 / 影响分析
      {
        path: 'mission/scheduler/jobs/:jobId/lineage',
        name: 'governance-scheduler-job-lineage',
        component: () => import('@/views/governance/JobLineageView.vue'),
        meta: { title: 'Job 血缘分析', menuGroup: 'ops-center' }
      },
      // V14 Chunk 4：scheduler 执行报表
      {
        path: 'mission/scheduler/reports',
        name: 'governance-scheduler-reports',
        component: () => import('@/views/governance/SchedulerReportsView.vue'),
        meta: { title: 'Scheduler 执行报表', menuGroup: 'ops-center' }
      },
      // V10.2 W2.3：scheduler 变更申请审批中心
      {
        path: 'mission/scheduler/change-requests',
        name: 'governance-scheduler-change-requests',
        component: () => import('@/views/governance/ChangeRequestsView.vue'),
        meta: { title: '变更申请审批', menuGroup: 'ops-center' }
      },
      // 2026-04-23: /mission/task-engine 已合并到 /mission/scheduler（ADR-007 更新）
      // platform-service 后端 task-engine API 保留，前端通过 scheduler 的 exec_type=etl_skill 复用
      {
        path: 'mission/task-engine',
        redirect: '/mission/scheduler',
      },
      // 工作流管理
      {
        path: 'mission/workflows',
        name: 'WorkflowList',
        component: () => import('@/views/WorkflowListView.vue'),
        meta: { title: '工作流编排', menuGroup: 'ops-center' }
      },
      {
        path: 'mission/workflow-tools',
        name: 'WorkflowTools',
        component: () => import('@/views/WorkflowToolsView.vue'),
        meta: { title: '工作流工具', menuGroup: 'ops-center' }
      },
      {
        path: 'mission/model-management',
        name: 'WorkflowModelManagement',
        component: () => import('@/views/governance/ModelConfigView.vue'),
        meta: { title: '模型配置', menuGroup: 'model-mgmt' }
      },
      {
        path: 'mission/workflows/:id/editor',
        name: 'WorkflowEditor',
        component: () => import('@/views/WorkflowEditorView.vue'),
        meta: { title: '工作流编辑器', menuGroup: 'ops-center', hideSidebar: true }
      },
      {
        path: 'mission/workflows/:id/overview',
        name: 'WorkflowOverview',
        component: () => import('@/views/WorkflowOverviewView.vue'),
        meta: { title: '工作流概览', menuGroup: 'ops-center', hideSidebar: true }
      },
      {
        path: 'mission/workflows/run/:runId',
        name: 'WorkflowRun',
        component: () => import('@/views/WorkflowRunView.vue'),
        meta: { title: '工作流运行', menuGroup: 'ops-center', hideSidebar: true }
      },
      {
        path: 'mission/workflows/:id/chat',
        name: 'WorkflowChatflow',
        component: () => import('@/views/ChatflowView.vue'),
        meta: { title: 'Chatflow', menuGroup: 'ops-center', hideSidebar: true }
      },
      // ===== 增长策略路由（mission/growth 域）=====
      {
        path: 'mission/growth/strategies',
        name: 'growth-strategy-list',
        component: () => import('@/views/governance/marketing/MarketingStrategyListView.vue'),
        meta: { title: '增长策略', menuGroup: 'workbench' }
      },
      {
        path: 'mission/growth/strategies/:strategyId',
        name: 'growth-strategy-detail',
        component: () => import('@/views/governance/marketing/MarketingStrategyDetailView.vue'),
        meta: { title: '增长策略详情', menuGroup: 'workbench' }
      },
      {
        path: 'mission/growth/materials',
        name: 'competitor-materials',
        component: () => import('@/views/governance/marketing/CompetitorMaterialView.vue'),
        meta: { title: '竞品素材', menuGroup: 'workbench' }
      },
      {
        path: 'mission/channel-management',
        name: 'channel-management',
        component: () => import('@/views/governance/channel-monitor/ChannelManagementView.vue'),
        meta: { title: '渠道管理', menuGroup: 'workbench' }
      },
      // 旧营销路由重定向
      {
        path: 'mission/marketing/strategies',
        redirect: '/mission/growth/strategies'
      },
      // 监控规则已合并到系统监控 → 告警配置中
      {
        path: 'control/users',
        name: 'governance-users',
        component: () => import('@/views/governance/UserManagementView.vue'),
        meta: { title: '用户管理', menuGroup: 'platform-mgmt', requiresAdmin: true }
      },
      {
        path: 'control/user-management',
        name: 'governance-user-management',
        component: () => import('@/views/governance/UserManagementView.vue'),
        meta: { title: '用户管理', menuGroup: 'platform-mgmt', requiresAdmin: true }
      },
      // 会话时间线降级为执行概览的 Drawer，旧路由重定向（timeline=1 标记
      // 显式请求时间线抽屉，与「查看追踪」的 ?session_id 深链区分）
      {
        path: 'mission/session-timeline',
        redirect: to => ({ path: '/mission/tracing', query: { ...to.query, timeline: '1' } })
      },
      // ===== 智能体管理路由（agent 域）=====
      {
        path: 'agent/agents',
        name: 'agent-list',
        component: () => import('@/views/agent-management/AgentListView.vue'),
        meta: { title: '智能体目录', menuGroup: 'agent-mgmt' }
      },
      {
        path: 'agent/agents/capabilities',
        name: 'capability-config',
        component: () => import('@/views/agent-management/CapabilityConfigView.vue'),
        meta: { title: '能力配置', menuGroup: 'agent-mgmt' }
      },
      {
        path: 'agent/agents/prompt-engineering',
        name: 'prompt-engineering',
        component: () => import('@/views/agent-management/PromptEngineeringView.vue'),
        meta: { title: '提示词工程', menuGroup: 'agent-mgmt' }
      },
      {
        path: 'agent/agents/feedback',
        name: 'feedback-dashboard',
        component: () => import('@/views/agent-management/FeedbackDashboard.vue'),
        meta: { title: '反馈看板', menuGroup: 'agent-mgmt' }
      },
      {
        // 静态路由须放在 :agentId 动态路由之前，避免被当成 agentId 匹配
        path: 'agent/agents/model-settings',
        name: 'agent-model-settings',
        component: () => import('@/views/agent-management/ModelSettingsView.vue'),
        meta: { title: '模型路由', menuGroup: 'agent-mgmt' }
      },
      {
        path: 'agent/agents/:agentId/instances',
        name: 'agent-instance-list',
        component: () => import('@/views/agent-management/AgentInstanceListView.vue'),
        meta: { title: '智能体实例', menuGroup: 'agent-mgmt' }
      },
      {
        path: 'agent/agents/:agentId',
        name: 'agent-detail',
        component: () => import('@/views/agent-management/AgentDetailView.vue'),
        meta: { title: '智能体配置', menuGroup: 'agent-mgmt' }
      },
      // 模型成本归入管控中心
      {
        path: 'control/model-cost',
        name: 'governance-model-cost',
        component: () => import('@/views/governance/CostDashboardView.vue'),
        meta: { title: '模型成本', menuGroup: 'admin', requiresAdmin: true }
      },
      // ===== 旧路由重定向（兼容性）=====
      {
        path: 'agent/agents/templates',
        redirect: '/agent/agents'
      },
      {
        path: 'agent/agents/skills',
        redirect: '/agent/agents/capabilities'
      },
      {
        path: 'agent/agents/mcp',
        redirect: '/agent/agents/capabilities'
      },
      {
        path: 'agent/agents/evaluation',
        redirect: '/agent/agents/quality'
      },
      {
        path: 'agent/agents/history',
        redirect: '/mission/tracing'
      },
      // ===== 智能体协作路由 =====
      {
        path: 'agents/data-analyst',
        name: 'data-analyst-agent',
        component: () => import('@/views/DataAnalystAgentView.vue'),
        meta: { title: '数据分析智能体', menuGroup: 'agent-mgmt', feature: 'chatbi_v3' }
      },
      {
        path: 'agents/dashboard',
        name: 'dashboard-generator',
        component: () => import('@/views/DashboardGeneratorView.vue'),
        meta: { title: '仪表板生成', menuGroup: 'agent-mgmt', feature: 'chatbi_v3' }
      },
      {
        path: 'agents/image-generator',
        name: 'image-generator',
        component: () => import('@/views/ImageGeneratorView.vue'),
        meta: { title: '图像生成智能体', menuGroup: 'agent-mgmt' }
      },
      {
        path: 'agents/tiangong',
        name: 'tiangong-agent',
        component: () => import('@/views/TiangongAgentView.vue'),
        meta: { title: '天工智能体', menuGroup: 'agent-mgmt' }
      },
      {
        path: 'creative-workshop',
        name: 'creative-workshop',
        component: () => import('@/components/business/creative/CreativeWorkshopView.vue'),
        meta: { title: '创意工坊', menuGroup: 'agent-mgmt' }
      },
      // ===== 业务智能体旧路由重定向到统一工作台 =====
      { path: 'agents/a/:agentId', redirect: (to) => `/agents/workspace?agent=${(to.params as Record<string, string>).agentId}` },
      { path: 'agents/customer-service',  redirect: '/agents/workspace?agent=cs' },
      { path: 'agents/creative',          redirect: '/agents/workspace?agent=creative' },
      // ===== 其他路由 =====
      {
        path: 'analysis/event',
        name: 'event-analysis',
        component: () => import('@/views/EventAnalysisView.vue'),
        meta: { title: '事件分析', menuGroup: 'data' }
      },
      {
        path: 'daily-report',
        name: 'daily-report',
        component: () => import('@/views/DailyReportView.vue'),
        meta: { title: '日报', menuGroup: 'ops-center' }
      },
      {
        path: 'agents/workspace',
        name: 'agent-workspace',
        component: () => import('@/views/UnifiedAgentView.vue'),
        meta: { title: '智能体工作台', menuGroup: 'agent-workspace' }
      },
      // Deeplink 快捷入口：/chat?agent=xxx&q=xxx&ctx=xxx 重定向到工作台
      // 站内通知的 action_url 使用此路径，保持 URL 简洁
      {
        path: 'chat',
        redirect: (to) => ({ path: '/agents/workspace', query: to.query }),
      },
      // 旧路由重定向
      { path: 'agents/data-analytics', redirect: '/agents/workspace' },
      { path: 'agents/social-media', redirect: '/agents/workspace?agent=social_media' },
      { path: 'agents/research', redirect: '/agents/workspace?agent=research' },
      // 智能体交互演示页面
      {
        path: 'agent-interaction-demo',
        name: 'agent-interaction-demo',
        component: () => import('@/components/chat/agent-interaction/AgentInteractionDemo.vue'),
        meta: { title: '智能体交互演示', menuGroup: 'agent-mgmt' }
      },
      // 智能体输出样式页面
      {
        path: 'agent-output',
        name: 'agent-output',
        component: () => import('@/components/chat/agent-interaction/AgentOutputView.vue'),
        meta: { title: '智能体输出样式', menuGroup: 'agent-mgmt' }
      },
    ]
  }
]

applyRoutePermissions(routes)

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

export const routePermissionAuditEntries = flattenRoutesForPermissionAudit(routes)

// 路由守卫：检查认证状态，未认证时重定向到登录页
let authInitialized = false

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // 首次导航时尝试从持久化存储恢复认证状态
  if (!authInitialized) {
    authInitialized = true
    if (!authStore.isAuthenticated) {
      await authStore.initAuth()
    }
  }

  // 公开路由直接放行（登录页等）
  if (to.name && PUBLIC_ROUTE_NAMES.has(to.name as string)) {
    // 已登录用户访问登录页时，直接跳到首页
    if (authStore.isAuthenticated && to.name === 'login') {
      next({ path: DEFAULT_ROUTE })
      return
    }
    next()
    return
  }

  // 已认证：检查权限
  if (authStore.isAuthenticated) {
    // 依赖已下线能力的页面（ChatBI V3：数据分析智能体、仪表板生成，任务 37）拦回首页
    const feature = to.matched.map(r => r.meta?.feature).find(Boolean)
    if (feature && !(await getFeatures())[feature]) {
      logger.warn(`[路由守卫] ${to.fullPath} 依赖的能力 ${feature} 已下线，回首页`)
      next({ path: DEFAULT_ROUTE })
      return
    }
    if (!authStore.canAccessRoute(to)) {
      const required = requiredPermissionsText(to)
      logger.warn(`[路由守卫] 已认证用户权限不足: ${to.fullPath}，已进入 403`)
      next({
        name: 'forbidden',
        query: {
          from: to.fullPath,
          required,
        },
      })
      return
    }
    next()
    return
  }

  // 未认证 → 重定向到登录页，并记录原始目标路由
  logger.warn(`[路由守卫] 未认证用户访问受保护路由: ${to.fullPath}，已重定向到登录页`)
  next({ name: 'login', query: { redirect: to.fullPath } })
})

export default router
